/**
 * `subscriptions` — facturation Artosera, tenue par Venio (base plateforme, 4.1).
 *
 * Deux natures (vue d'aigle, ARTO-58) : l'abonnement d'UNE galerie à un
 * palier, ou le forfait d'un exploitant (`groupeId`) pour ses galeries.
 * Aucun tarif n'est écrit ici : un montant non saisi vaut `null`, s'affiche
 * « à saisir » et n'entre dans aucun total.
 */
import { z } from 'zod';
import { palierSchema } from '../modules.js';
import { centimesPositifsSchema, dateSaisieSchema, deviseSchema, idSchema } from './commun.js';
import { champsSuiviPlateforme, TAUX_TVA_RECETTE_PAR_DEFAUT, tauxTvaRecetteSchema, } from './pilotage.js';
export const ETATS_ABONNEMENT = ['essai', 'actif', 'suspendu', 'resilie'];
export const etatAbonnementSchema = z.enum(ETATS_ABONNEMENT);
export const NATURES_ABONNEMENT = ['galerie', 'forfait_exploitant'];
export const natureAbonnementSchema = z.enum(NATURES_ABONNEMENT);
/**
 * Les champs SANS valeur par défaut : c'est la forme d'une mise à jour, où un
 * champ absent doit rester absent — un défaut y réactiverait un abonnement
 * suspendu ou effacerait un montant saisi.
 */
const champsAbonnement = z.object({
    nature: natureAbonnementSchema,
    /** L'exploitant d'un forfait ; « absent » se dit `null`, comme en base. */
    groupeId: idSchema.nullable(),
    galeries: z.array(idSchema).min(1),
    palier: palierSchema.nullable(),
    engagementMois: z.int().min(0),
    debutLe: dateSaisieSchema,
    finLe: dateSaisieSchema.nullable(),
    /** `null` = montant à saisir : il n'entre dans aucun total (ARTO-58). */
    montantMensuelCentimes: centimesPositifsSchema.nullable(),
    devise: deviseSchema,
    remisePointsDeBase: z.int().min(0).max(10_000),
    etat: etatAbonnementSchema,
    /**
     * TVA de la recette, en points de base : le montant mensuel est HORS TAXES.
     * Le taux normal par défaut (la loi, pas un tarif) ; 0 pour une
     * autoliquidation ou un client hors de France.
     */
    tauxTvaPointsDeBase: tauxTvaRecetteSchema,
});
/** Les mêmes champs, avec leurs valeurs par défaut : la forme d'une création. */
const champsAbonnementCrees = champsAbonnement.extend({
    nature: champsAbonnement.shape.nature.default('galerie'),
    groupeId: champsAbonnement.shape.groupeId.default(null),
    palier: champsAbonnement.shape.palier.default(null),
    finLe: champsAbonnement.shape.finLe.default(null),
    engagementMois: champsAbonnement.shape.engagementMois.default(12),
    montantMensuelCentimes: champsAbonnement.shape.montantMensuelCentimes.default(null),
    devise: champsAbonnement.shape.devise.default('EUR'),
    remisePointsDeBase: champsAbonnement.shape.remisePointsDeBase.default(0),
    etat: champsAbonnement.shape.etat.default('actif'),
    tauxTvaPointsDeBase: tauxTvaRecetteSchema.default(TAUX_TVA_RECETTE_PAR_DEFAUT),
});
/**
 * Un abonnement de galerie a un palier ; un forfait a son exploitant.
 *
 * Une mise à jour partielle ne peut pas le vérifier seule : la route le
 * contrôle sur le document fusionné.
 */
function coherenceNature(abonnement, ctx) {
    if (abonnement.nature === 'galerie' && abonnement.palier == null) {
        ctx.addIssue({ code: 'custom', path: ['palier'], message: 'Palier requis.' });
    }
    if (abonnement.nature === 'forfait_exploitant' && abonnement.groupeId == null) {
        ctx.addIssue({ code: 'custom', path: ['groupeId'], message: 'Exploitant requis.' });
    }
}
/** Le document stocké : auteurs à la façon de la base plateforme. */
export const subscriptionSchema = champsAbonnementCrees
    .extend({
    ...champsSuiviPlateforme,
    origine: z.enum(['saisie', 'facturation']).optional(),
    souscriptionCoeurId: idSchema.optional(),
})
    .superRefine(coherenceNature);
export const subscriptionCreationSchema = champsAbonnementCrees.superRefine(coherenceNature);
export const subscriptionMiseAJourSchema = champsAbonnement.partial();
//# sourceMappingURL=subscriptions.js.map